import React, { useState, useRef, useEffect } from 'react';
import { 
  Box, 
  IconButton, 
  LinearProgress, 
  Typography,
  Tooltip
} from '@mui/material';
import { 
  PlayArrow as PlayIcon, 
  Pause as PauseIcon,
  VolumeUp as VolumeIcon,
  VolumeOff as MuteIcon,
  MusicNote as MusicIcon,
  OpenInNew as OpenInSpotifyIcon
} from '@mui/icons-material';

interface AudioPlayerProps {
  previewUrl?: string;
  trackName: string;
  artistName: string;
  spotifyUri?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ 
  previewUrl, 
  trackName, 
  artistName,
  spotifyUri
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    if (previewUrl) {
      const audio = new Audio(previewUrl);
      audioRef.current = audio;
      
      // Set up event listeners
      audio.addEventListener('timeupdate', updateProgress);
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration);
      });
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setProgress(0);
      });
      
      return () => {
        // Clean up
        audio.pause();
        audio.removeEventListener('timeupdate', updateProgress);
        audio.removeEventListener('loadedmetadata', () => {});
        audio.removeEventListener('ended', () => {});
      };
    }
  }, [previewUrl]);

  // Update progress bar
  const updateProgress = () => {
    if (audioRef.current) {
      const value = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setProgress(value);
    }
  };

  // Toggle play/pause
  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // Stop any other playing audio elements first
      document.querySelectorAll('audio').forEach(audio => audio.pause());
      audioRef.current.play();
    }
    
    setIsPlaying(!isPlaying);
  };

  // Toggle mute
  const toggleMute = () => {
    if (!audioRef.current) return;
    
    audioRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  // Open in Spotify
  const openInSpotify = () => {
    if (spotifyUri) {
      window.open(`https://open.spotify.com/track/${spotifyUri.split(':')[2]}`, '_blank');
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', my: 1 }}>
      {previewUrl ? (
        <>
          <IconButton 
            onClick={togglePlay} 
            color="primary"
            size="small"
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
          
          <Box sx={{ mx: 1, flexGrow: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 4, borderRadius: 2 }} 
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {Math.floor(duration * (progress / 100))}s
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {Math.floor(duration)}s
              </Typography>
            </Box>
          </Box>
          
          <IconButton 
            onClick={toggleMute}
            size="small"
          >
            {isMuted ? <MuteIcon fontSize="small" /> : <VolumeIcon fontSize="small" />}
          </IconButton>
        </>
      ) : (
        <Tooltip title="No preview available">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <MusicIcon color="disabled" sx={{ mr: 1 }} />
            <Typography variant="caption" color="text.secondary">
              No preview
            </Typography>
          </Box>
        </Tooltip>
      )}
      
      {spotifyUri && (
        <Tooltip title="Open in Spotify">
          <IconButton 
            onClick={openInSpotify}
            size="small"
            sx={{ ml: 1 }}
          >
            <OpenInSpotifyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default AudioPlayer;
